import { EquipmentType } from '../equipment/equipment.model';
import {
  TRIP_COVERAGE_STATUS,
  TRIP_DRIVER_PAY_TYPE,
  TRIP_DRIVER_PAY_UNIT,
  TripSearchDriverResponse,
  TRIP_STATUS,
  TripStop
} from '../trips/trips.model';
import { BOOKING_TYPE, ORDER_STATUS, OrderRate } from '../orders/orders.model';
import { INVOICE_STATUS } from '../../../providers/Invoices/Invoices.model';
import { LOAD_TYPE } from '../load/load.model';
import { FUEL_SOURCE, FUEL_STATUS } from '../fuel/fuel.model';
import { ISearchTagsResponse } from '../loadTags/loadTags.model';

export interface ISettlementSearchParams {
  offset?: number;
  limit?: number;
  body?: {
    startDate?: string;
    endDate?: string;
    driverIds?: string[];
  };
}

export enum SettlementStatus {
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  PAID = 'PAID'
}

export enum SettlementFrontendStatus {
  DRAFT = 'Not started',
  IN_PROGRESS = 'Draft',
  PAID = 'Paid'
}

export const SettlementStatusColor = {
  DRAFT: '#F3F4F6',
  IN_PROGRESS: '#3A4DE9',
  PAID: '#10B981'
};

export interface ISearchSettlement {
  offset?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  body?: {
    driverIds?: string[];
  };
  includeLegacy: boolean;
}

export interface IExportSettlements {
  startDate?: string;
  endDate?: string;
  body?: {
    driverIds?: string[];
    settlementIds?: string[];
  };
  includeLegacy: boolean;
}

export interface IGenerateSettlement {
  start?: string;
  end?: string;
  driverIds?: string[];
  offset?: number;
  limit?: number;
  status?: SettlementStatus;
}
export interface GenerateCustomSettlementResponse {
  data: SettlementBuilderResponse;
  more_records: boolean;
  success: boolean;
  total: number;
}

export interface SettlementsBuilderTable {
  adjustments: number;
  base_pay: number;
  deductions: number;
  driver_id: string;
  driver_name: string;
  has_settlement_trip: boolean;
  missing_driver_pay: boolean;
  net_pay: null | number;
  order_revenue: number;
  other_pay: number;
  status: SettlementStatus;
  trip_count: string;
  trip_ids: string;
  settlement_id?: string;
  loaded_miles: number | null;
  empty_miles: number | null;
}

export interface SettlementsSavedTable extends SettlementsBuilderTable {
  period_start_date: string | null;
  period_end_date: string | null;
  pdf_s3_signed_url?: string;
}

export interface SettlementBuilderResponse {
  settlements: SettlementsSavedTable[];
  totalBasePay: number | null;
  totalDeductions: number | null;
  totalLoadRevenue: number | null;
  totalNetPay: number | null;
  totalOtherPay: number | null;
  totalMileage: number | null;
}

export interface SettlementsSavedResponse {
  settlements: SettlementsSavedTable[];
  totalBasePay: number | null;
  totalDeductions: number | null;
  totalLoadRevenue: number | null;
  totalNetPay: number | null;
  totalOtherPay: number | null;
  totalMileage: number | null;
}

export interface SettlementDetailRequest {
  params: {
    start: string;
    end: string;
  };
  driverId: string;
}

export interface SettlementDetailDriverPay {
  id?: string;
  driver_id?: string;
  trip_id?: string;
  description?: string | null;
  pay_type?: TRIP_DRIVER_PAY_TYPE;
  unit?: TRIP_DRIVER_PAY_UNIT;
  quantity?: number;
  rate?: number;
  loaded_miles?: number | null;
  total_loaded_miles?: number | null;
  empty_miles?: number | null;
  overriden_order_rate?: number | null;
  trip_revenue_percentage?: number | null;
  gross_pay_estimate?: number | null;
  is_default?: boolean;
  qbo_service_id?: string | null;
  invoice_service_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  deleted_at?: string | null;
  driver_settlement_id?: string | null;
  load_id?: string | null;
}

export interface SettlementOrderInTripDetail {
  archive: boolean;
  booking_type: BOOKING_TYPE;
  broker_id: string;
  client_id: string;
  created_by: string;
  customer_contact_id: string | null;
  customer_order_request_id: string | null;
  deleted_at: string | null;
  dispatcher_id: string;
  division_id: string;
  edi_load_tender_id: string | null;
  estimated_mileage: number | null;
  id: string;
  identifier: string;
  instructions: string;
  invoice_status: INVOICE_STATUS;
  is_brokered_order: boolean;
  customer_order_number: string;
  order_rates: OrderRate[];
  order_type: LOAD_TYPE;
  payable_amount: number | null;
  primary_client: string;
  qbo_attributes: any; // todo: CHECK TYPE
  rate: number | null;
  rate_per_mile: number | null;
  readable_id: number;
  status: ORDER_STATUS;
  tenant_id: string;
}
export interface SettlementTripDetail {
  carrier_id?: string | null;
  coverage: TRIP_COVERAGE_STATUS;
  delivery_location: string;
  delivery_stop: TripStop;
  dispatch_note_count: number;
  drivers: TripSearchDriverResponse[];
  empty_miles: number | null;
  estimated_mileage: number | null;
  id: string;
  identifier: string | null;
  instructions: string;
  intermediate_stops: TripStop[];
  is_empty_miles_auto: boolean;
  is_loaded_miles_auto: boolean;
  order_trip_overview: {
    percent_mileage: number;
  };
  orders: {
    id: string;
    order: SettlementOrderInTripDetail;
    order_id: string;
    trip_id: string;
    updated_at: string;
  }[];
  pickup_location: string;
  pickup_stop: TripStop;
  readable_id: number;
  sort_order: number;
  sorted_stops: TripStop[];
  status: TRIP_STATUS;
  stops: TripStop[];
  tenant_id: string;
  trailer_id: string | null;
  trip_rate: number | null;
  truck_id: string | null;
  tags: ISearchTagsResponse[];
}

export interface SavedSettlementAdjustment {
  created_at: string;
  created_by: string;
  description: string;
  driver_id: string;
  driver_settlement_id: string;
  driver_settlement_trip_id: null | string;
  id: string;
  is_default: boolean;
  pay_type: TRIP_DRIVER_PAY_TYPE;
  quantity: number;
  rate: number;
  unit: TRIP_DRIVER_PAY_UNIT;
  updated_at: string;
  updated_by: string;
}
export interface SettlementDetail {
  trip_id: string;
  missing_driver_pay: boolean;
  has_settlement_trip: boolean;
  trip_driver_other_pay: SettlementDetailDriverPay[];
  trip_driver_base_pay: SettlementDetailDriverPay[];
  order_revenue: number;
  trip?: SettlementTripDetail;
  driver_settlement_id?: string;
}

export interface SettlementDetailFuelDeductions {
  id: string;
  tenant_id: string;
  transaction_date: string;
  driver_id: string;
  truck_id: string;
  state: string;
  item_description: string;
  quantity: number;
  price: number;
  amount: number;
  source: FUEL_SOURCE;
  status: FUEL_STATUS;
  transaction_import_id: string;
  is_fuel_transaction: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  is_included_on_settlement: boolean;
  truck: {
    id: string;
    type: EquipmentType;
    name: string;
    eld_connection_id: string | null;
    asset_eld_id: string | null;
    tenant_id: string;
    deleted_at: string | null;
  };
  driver: {
    name: string;
    initials: string;
    phone: string;
    readable_pay_rate_type: string | null;
    formatted_pay_rate: string | null;
    id: string;
    first_name: string;
    last_name: string;
    email_address: string;
    company_name: string | null;
    default_pay_rate_type: string | null;
    pay_rate: number | null;
    type: string | null;
    default_truck_id: string | null;
    default_trailer_id: string | null;
    carrier_id: string | null;
    tenant_id: string;
    twuser_id: string;
    deduct_fuel_purchase_on_settlement: boolean;
    deleted_at: string | null;
  };
}

export interface SettlementDetailProps {
  trips: SettlementDetail[];
  fuelDeductions: SettlementDetailFuelDeductions[];
  saveDriverAdjustmentsAsDefault?: boolean;
}

export interface ActiveSettlementSlice extends SettlementDetailProps {
  driver_id?: string;
  driver_name?: string;
  status?: SettlementStatus;
  period_start_date?: string;
  period_end_date?: string;
  adjustments?: SavedSettlementAdjustment[];
  driver?: any;
  pdf_s3_signed_url?: string;
}

export interface SettlementAdjustments {
  description?: string;
  rate?: number;
  quantity?: number;
  unit?: TRIP_DRIVER_PAY_UNIT;
  pay_type?: TRIP_DRIVER_PAY_TYPE;
}
export interface SaveSettlementsTripRequest {
  tripId: string;
  saveTripDriverOtherPayAsDefault: boolean;
  tripDriverBasePay: SettlementDetailDriverPay | null;
  tripDriverOtherPay: SettlementDetailDriverPay[] | null;
}
export interface SaveSettlementRequest {
  driverId: string;
  startDate: string;
  endDate: string;
  saveDriverAdjustmentsAsDefault: boolean;
  trips: SaveSettlementsTripRequest[];
  adjustments: SettlementAdjustments[];
  fuelDeductions: string[];
}

// specific other pay items
export type ISettlementDriverLoadAdjustmentType = IOtherLoadPayItemPayload &
  IOtherLoadPayItemResponse;

export enum ExtraPayUnit {
  PER_MILE = 'Per mile',
  FLAT = 'Flat',
  PER_HOUR = 'Per hour',
  PER_LOAD = 'Per load',
  PER_STOP = 'Per stop',
  PER_DIEM = 'Per diem'
}

export enum ExtraPayTypeOptions {
  OTHER_PAY = 'Other pay',
  REIMBURSEMENT = 'Reimbursement',
  DEDUCTION = 'Deduction'
}

export interface IOtherLoadPayItemPayload {
  id?: string;
  description?: string;
  pay_type: TRIP_DRIVER_PAY_TYPE;
  unit: ExtraPayUnit;
  quantity: number;
  rate: number;
  is_default?: boolean;
}

export interface IOtherLoadPayItemResponse {
  id?: string;
  description?: string;
  unit: ExtraPayUnit;
  quantity: number;
  rate: number;
  adjustment_level?: string;
  pay_type: TRIP_DRIVER_PAY_TYPE;
  adjustment_type: TRIP_DRIVER_PAY_TYPE;
  is_default?: boolean;
}
export interface SavedSettlementResponse {
  fuelDeductions: SettlementDetailFuelDeductions[];
  settlement: ActiveSettlementSlice;
  hasDeletedTrips: boolean;
}

export interface SettlementInSearchTrip {
  driver_id: string;
  driver_settlement_period_id: string;
  id: string;
  status: SettlementStatus;
  tenant_id: string;
  updated_at: string;
  created_at: string;
  created_by: string;
  updated_by: string;
  deleted_at: string | null;
}
